import { useEffect, type RefObject } from 'react'

/**
 * Keep wheel scrolling inside `el` when it can absorb movement, so host-page
 * wheel handlers (often non-passive) are not invoked while using the chat list.
 */
export function useWheelScrollIsolation(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current
    if (!el) return

    const onWheel = (e: WheelEvent) => {
      const canScrollY = el.scrollHeight > el.clientHeight + 1
      const canScrollX = el.scrollWidth > el.clientWidth + 1
      if (!canScrollY && !canScrollX) return

      const { scrollTop, scrollHeight, clientHeight, scrollLeft, scrollWidth, clientWidth } = el
      const atTop = scrollTop <= 0
      const atBottom = scrollTop + clientHeight >= scrollHeight - 1
      const atLeft = scrollLeft <= 0
      const atRight = scrollLeft + clientWidth >= scrollWidth - 1

      if (canScrollY) {
        const goingUp = e.deltaY < 0
        const goingDown = e.deltaY > 0
        if (!(atTop && goingUp) && !(atBottom && goingDown)) {
          e.stopPropagation()
          return
        }
      }
      if (canScrollX) {
        const goingLeft = e.deltaX < 0
        const goingRight = e.deltaX > 0
        if (!(atLeft && goingLeft) && !(atRight && goingRight)) {
          e.stopPropagation()
        }
      }
    }

    el.addEventListener('wheel', onWheel, { passive: true, capture: true })
    return () => el.removeEventListener('wheel', onWheel, { capture: true })
  }, [ref])
}
